import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import {
  ChevronLeft, Save, FileText, Settings, Eye, EyeOff, X,
  Zap, Layers, Megaphone, Plus, Minus, Sparkles,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const ADMIN_ROLES = new Set(["COO", "Admin"]);
const fmt = (n) => "$" + Math.round(Number(n) || 0).toLocaleString();

// Margin presets — applied as markup over vendor cost
const MARGIN_PRESETS = [30, 40, 50];

// Reel pricing keys (look up by service name in admin catalog)
const REEL_NAMES = {
  short: "15–20 Second Reel",
  medium: "30–45 Second Reel",
  long: "45–60 Second Reel",
};

// Plan 3 intensity → existing service in catalog
const FULL_ENGINE_MAPPING = {
  growth: { serviceName: "3 Reels/Week (12/mo)", reels: 12, label: "Growth" },
  scale: { serviceName: "Daily Reels (28–30/mo)", reels: 30, label: "Scale" },
  dominance: { serviceName: "Daily Reels (28–30/mo)", reels: 30, label: "Dominance" },
};

function marginTier(pct) {
  if (pct >= 50) return { label: "Strong", color: "#4CA173" };
  if (pct >= 35) return { label: "Healthy", color: "#9E9B96" };
  if (pct >= 20) return { label: "Low", color: "#C9835F" };
  return { label: "Risk", color: "#C24A2E" };
}

const PLATFORM_MGMT_PER = 600; // vendor cost per platform/month for management layer
const AD_CAMPAIGN_VENDOR = 800; // vendor cost for optional ad campaign mgmt

export default function AIContentCalculator() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = quoteId === "new" || !quoteId;
  const isAdmin = ADMIN_ROLES.has(user?.role);

  const [services, setServices] = useState([]);
  const [showInternal, setShowInternal] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proposalHtml, setProposalHtml] = useState(null);
  const [loading, setLoading] = useState(true);

  // Project info
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [projectType, setProjectType] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");

  // Plan selection
  const [plan, setPlan] = useState("content_only"); // content_only | content_mgmt | full_engine
  const [months, setMonths] = useState(6);
  const [markup, setMarkup] = useState(50);

  // Plan 1: Content Only
  const [reelsPerMonth, setReelsPerMonth] = useState(8);
  const [autoMix, setAutoMix] = useState(true);
  const [shortQty, setShortQty] = useState(0);
  const [mediumQty, setMediumQty] = useState(0);
  const [longQty, setLongQty] = useState(0);

  // Plan 2: Content + Management
  const [mgmtReelsPerMonth, setMgmtReelsPerMonth] = useState(12);
  const [platforms, setPlatforms] = useState(1);
  const [adCampaign, setAdCampaign] = useState(false);

  // Plan 3: Full Content Engine
  const [intensity, setIntensity] = useState("growth"); // growth | scale | dominance

  // Load services (admin-managed pricing)
  const fetchServices = useCallback(async () => {
    try {
      const res = await apiClient.get("/ai-content/services");
      setServices(res.data || []);
    } catch {
      toast.error("Failed to load AI Content pricing");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchServices(); }, [fetchServices]);

  // Load existing quote
  useEffect(() => {
    if (isNew) return;
    apiClient.get(`/ai-content/quotes/${quoteId}`).then((res) => {
      const q = res.data;
      setProjectName(q.project_name || "");
      setClientName(q.client_name || "");
      setProjectType(q.project_type || "");
      setLocation(q.location || "");
      setNotes(q.notes || "");
      const meta = q.payment_terms?.builder_meta;
      if (meta) {
        setPlan(meta.plan || "content_only");
        setMonths(meta.months || 6);
        setMarkup(meta.markup ?? 50);
        setReelsPerMonth(meta.reelsPerMonth || 8);
        setAutoMix(!!meta.autoMix);
        setShortQty(meta.shortQty || 0);
        setMediumQty(meta.mediumQty || 0);
        setLongQty(meta.longQty || 0);
        setMgmtReelsPerMonth(meta.mgmtReelsPerMonth || 12);
        setPlatforms(meta.platforms || 1);
        setAdCampaign(!!meta.adCampaign);
        setIntensity(meta.intensity || "growth");
      }
    }).catch(() => toast.error("Failed to load quote"));
  }, [quoteId, isNew]);

  // Service price lookup
  const reelCost = useCallback((duration) => {
    if (!isAdmin) return null;
    const svc = services.find(s => s.name === REEL_NAMES[duration]);
    return svc?.vendor_cost ?? null;
  }, [services, isAdmin]);

  // ------ Pricing engine ------
  const breakdown = useMemo(() => {
    // Plan 1: Content Only
    if (plan === "content_only") {
      let qShort, qMedium, qLong;
      if (autoMix) {
        // 60% medium / 25% short / 15% long
        qMedium = Math.round(reelsPerMonth * 0.6);
        qShort = Math.round(reelsPerMonth * 0.25);
        qLong = Math.max(0, reelsPerMonth - qMedium - qShort);
      } else {
        qShort = shortQty; qMedium = mediumQty; qLong = longQty;
      }
      const total = qShort + qMedium + qLong;
      // vendor cost per reel (admin only — for sales reps we still need monthly vendor for backend totals)
      const cShort = reelCost("short") ?? 225;
      const cMedium = reelCost("medium") ?? 375;
      const cLong = reelCost("long") ?? 495;
      const monthlyVendor = qShort * cShort + qMedium * cMedium + qLong * cLong;
      const monthlyClient = monthlyVendor * (1 + markup / 100);
      return {
        planLabel: "Content Only",
        reelsPerMonth: total,
        platforms: 0,
        monthlyVendor,
        monthlyClient,
        setupFee: 0,
        deliverables: [
          `${total} AI reels per month`,
          autoMix ? "Auto-mix duration mix" : `Short ${qShort} · Medium ${qMedium} · Long ${qLong}`,
          "Editing, music & sound design",
        ],
        mix: { qShort, qMedium, qLong },
      };
    }
    // Plan 2: Content + Management
    if (plan === "content_mgmt") {
      // Use content_management catalog as base
      const baseSvc = services.find(s => /(\d+)\/mo/.test(s.name) && s.category === "content_management" && s.monthly_reels === mgmtReelsPerMonth)
        || services.find(s => s.category === "content_management" && s.monthly_reels === mgmtReelsPerMonth);
      const baseVendor = baseSvc?.vendor_cost ?? (mgmtReelsPerMonth === 8 ? 2400 : mgmtReelsPerMonth === 12 ? 3600 : mgmtReelsPerMonth === 20 ? 4800 : 6800);
      // Platforms multiplier: +PLATFORM_MGMT_PER per extra platform
      const platformAdd = Math.max(0, platforms - 1) * PLATFORM_MGMT_PER;
      const adAdd = adCampaign ? AD_CAMPAIGN_VENDOR : 0;
      const monthlyVendor = baseVendor + platformAdd + adAdd;
      const monthlyClient = monthlyVendor * (1 + markup / 100);
      return {
        planLabel: "Content + Management",
        reelsPerMonth: mgmtReelsPerMonth,
        platforms,
        monthlyVendor,
        monthlyClient,
        setupFee: 0,
        adCampaign,
        deliverables: [
          `${mgmtReelsPerMonth} AI reels per month`,
          `${platforms} platform${platforms > 1 ? "s" : ""} managed`,
          "Posting · captions · hashtags · analytics",
          ...(adCampaign ? ["Paid ad campaign management"] : []),
        ],
      };
    }
    // Plan 3: Full Content Engine
    const map = FULL_ENGINE_MAPPING[intensity];
    const svc = services.find(s => s.name === map.serviceName);
    const baseVendor = svc?.vendor_cost ?? (intensity === "growth" ? 3600 : intensity === "scale" ? 6800 : 8500);
    const monthlyVendor = baseVendor;
    const monthlyClient = monthlyVendor * (1 + markup / 100);
    return {
      planLabel: `Full Content Engine — ${map.label}`,
      reelsPerMonth: map.reels,
      platforms: 2,
      monthlyVendor,
      monthlyClient,
      setupFee: 0,
      deliverables: [
        `${map.reels} AI reels per month`,
        "Full social media management",
        "Campaign planning & strategy",
        "Priority production queue",
        "Multi-platform distribution",
      ],
    };
  }, [plan, autoMix, reelsPerMonth, shortQty, mediumQty, longQty, mgmtReelsPerMonth, platforms, adCampaign, intensity, markup, services, reelCost]);

  // Totals across the contract length
  const totals = useMemo(() => {
    const totalClient = breakdown.monthlyClient * months + breakdown.setupFee;
    const totalVendor = breakdown.monthlyVendor * months + breakdown.setupFee;
    const profit = totalClient - totalVendor;
    const margin = totalClient > 0 ? (profit / totalClient) * 100 : 0;
    return { totalClient, totalVendor, profit, margin };
  }, [breakdown, months]);

  const tier = marginTier(totals.margin);

  // Build a single line item for backend persistence
  const buildLineItems = () => {
    return [{
      id: crypto.randomUUID(),
      service_id: "engine",
      name: breakdown.planLabel,
      category: plan === "content_only" ? "content_bundles"
              : plan === "content_mgmt" ? "content_management"
              : "bundled_packages",
      billing: "monthly",
      quantity: 1,
      months,
      vendor_cost: breakdown.monthlyVendor,
      pricing_mode: "percent",
      markup_percent: markup,
      markup_fixed: 0,
      manual_price: 0,
      setup_fee: breakdown.setupFee,
      monthly_reels: breakdown.reelsPerMonth,
    }];
  };

  // Save / Generate Proposal
  const handleSave = async () => {
    if (!projectName.trim()) { toast.error("Project name required"); return; }
    setSaving(true);
    try {
      const payload = {
        project_name: projectName,
        client_name: clientName,
        project_type: projectType,
        location,
        notes,
        salesperson: user?.name || "",
        line_items: buildLineItems(),
        payment_terms: {
          schedule: [],
          builder_meta: {
            plan, months, markup,
            reelsPerMonth, autoMix, shortQty, mediumQty, longQty,
            mgmtReelsPerMonth, platforms, adCampaign,
            intensity,
            deliverables: breakdown.deliverables,
            plan_label: breakdown.planLabel,
          },
        },
      };
      let res;
      if (isNew) {
        res = await apiClient.post("/ai-content/quotes", payload);
        toast.success("Quote saved");
        navigate(`/sales/calculators/ai-content/${res.data.id}`, { replace: true });
      } else {
        await apiClient.patch(`/ai-content/quotes/${quoteId}`, payload);
        toast.success("Quote updated");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleGenerateProposal = async () => {
    if (isNew) { toast.error("Save the quote first"); return; }
    try {
      const res = await apiClient.get(`/ai-content/quotes/${quoteId}/proposal`);
      setProposalHtml(res.data.html);
    } catch {
      toast.error("Failed to generate proposal");
    }
  };

  const showInternalView = isAdmin && showInternal;

  return (
    <div className="min-h-screen bg-[#000000] text-[#F4F2EE]" data-testid="ai-content-calculator">
      <div className="max-w-[1100px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4 min-w-0">
            <button onClick={() => navigate(-1)} className="text-[#8A8783] hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783]">TRP AI Content Studio</div>
              <h1 className="text-[24px] font-light tracking-tight">{projectName || "New Quote"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <Button variant="ghost" size="sm" onClick={() => setShowInternal(v => !v)} className="text-[#C9C6C1] hover:text-white" data-testid="toggle-internal-view">
                  {showInternal ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
                  {showInternal ? "Hide Margin" : "Show Margin"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate("/sales/calculators/ai-content/admin")} className="border-white/[0.10] bg-transparent text-[#C9C6C1] hover:bg-white/[0.06]" data-testid="open-ai-admin">
                  <Settings className="w-4 h-4 mr-1.5" /> Pricing Admin
                </Button>
              </>
            )}
            <Button onClick={handleGenerateProposal} variant="outline" size="sm" className="border-white/[0.10] bg-transparent text-[#C9C6C1] hover:bg-white/[0.06]" disabled={isNew} data-testid="generate-proposal-btn">
              <FileText className="w-4 h-4 mr-1.5" /> Proposal
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-white text-[#000000] hover:bg-white/90" data-testid="save-btn">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* LEFT */}
          <div className="space-y-6">
            {/* Project info — minimal */}
            <Card>
              <div className="grid grid-cols-2 gap-3 px-5 py-4">
                <Field label="Project Name">
                  <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} className="bg-[#000000] border-white/[0.08] text-white" data-testid="project-name" />
                </Field>
                <Field label="Client Name">
                  <Input value={clientName} onChange={(e) => setClientName(e.target.value)} className="bg-[#000000] border-white/[0.08] text-white" data-testid="client-name" />
                </Field>
              </div>
            </Card>

            {/* Plan selector */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-3">Select Content Plan</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <PlanCard
                  active={plan === "content_only"}
                  onClick={() => setPlan("content_only")}
                  icon={Zap}
                  title="Content Only"
                  tagline="Just the reels."
                  description="High-volume AI-driven reel production. You handle posting and strategy."
                  testId="plan-content-only"
                />
                <PlanCard
                  active={plan === "content_mgmt"}
                  onClick={() => setPlan("content_mgmt")}
                  icon={Layers}
                  title="Content + Management"
                  tagline="Production + posting."
                  description="Reels delivered, scheduled, and posted to your channels with captions & analytics."
                  testId="plan-content-mgmt"
                />
                <PlanCard
                  active={plan === "full_engine"}
                  onClick={() => setPlan("full_engine")}
                  icon={Sparkles}
                  title="Full Content Engine"
                  tagline="Done for you."
                  description="The complete operating system: production, management, campaign planning, priority queue."
                  testId="plan-full-engine"
                />
              </div>
            </div>

            {/* Plan-specific configuration */}
            <Card>
              <div className="px-5 py-4 border-b border-white/[0.06]">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-1">Configure</div>
                <div className="text-[15px] font-semibold text-[#F4F2EE]">{breakdown.planLabel}</div>
              </div>

              <div className="px-5 py-4 space-y-4">
                {plan === "content_only" && (
                  <>
                    <Field label="Reels per month">
                      <ChipPicker
                        options={[{v:4,l:"4"},{v:8,l:"8"},{v:12,l:"12"},{v:20,l:"20"}]}
                        value={reelsPerMonth}
                        onChange={setReelsPerMonth}
                        testIdPrefix="reels-per-month"
                      />
                    </Field>
                    <div className="flex items-center justify-between rounded-lg border border-white/[0.08] bg-[#000000] px-4 py-3">
                      <div>
                        <div className="text-[13px] text-[#F4F2EE]">Auto-mix duration</div>
                        <div className="text-[11px] text-[#8A8783]">25% short · 60% medium · 15% long</div>
                      </div>
                      <Switch checked={autoMix} onCheckedChange={setAutoMix} data-testid="auto-mix-toggle" />
                    </div>
                    {!autoMix && (
                      <div className="grid grid-cols-3 gap-2">
                        <Stepper label="Short (15–20s)" value={shortQty} onChange={setShortQty} />
                        <Stepper label="Medium (30–45s)" value={mediumQty} onChange={setMediumQty} />
                        <Stepper label="Long (45–60s)" value={longQty} onChange={setLongQty} />
                      </div>
                    )}
                  </>
                )}

                {plan === "content_mgmt" && (
                  <>
                    <Field label="Reels per month">
                      <ChipPicker
                        options={[{v:8,l:"8"},{v:12,l:"12"},{v:20,l:"20"},{v:30,l:"30"}]}
                        value={mgmtReelsPerMonth}
                        onChange={setMgmtReelsPerMonth}
                        testIdPrefix="mgmt-reels"
                      />
                    </Field>
                    <Field label="Platforms managed">
                      <ChipPicker
                        options={[{v:1,l:"1 Platform"},{v:2,l:"2 Platforms"}]}
                        value={platforms}
                        onChange={setPlatforms}
                        testIdPrefix="platforms"
                      />
                    </Field>
                    <div className="rounded-lg border border-white/[0.08] bg-[#000000] px-4 py-3">
                      <div className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-2">Included</div>
                      <ul className="text-[12px] text-[#C9C6C1] space-y-1">
                        <li>· Posting & scheduling</li>
                        <li>· Captions & hashtags</li>
                        <li>· Analytics reporting</li>
                      </ul>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-white/[0.08] bg-[#000000] px-4 py-3">
                      <div>
                        <div className="text-[13px] text-[#F4F2EE]">+ Ad Campaign Management</div>
                        <div className="text-[11px] text-[#8A8783]">Paid social ad strategy + creative iteration</div>
                      </div>
                      <Switch checked={adCampaign} onCheckedChange={setAdCampaign} data-testid="ad-campaign-toggle" />
                    </div>
                  </>
                )}

                {plan === "full_engine" && (
                  <>
                    <Field label="Content intensity">
                      <ChipPicker
                        options={[
                          { v: "growth", l: "Growth · 12/mo" },
                          { v: "scale", l: "Scale · 20/mo" },
                          { v: "dominance", l: "Dominance · 30/mo" },
                        ]}
                        value={intensity}
                        onChange={setIntensity}
                        testIdPrefix="intensity"
                      />
                    </Field>
                    <div className="rounded-lg border border-white/[0.08] bg-[#000000] px-4 py-3">
                      <div className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-2">Includes</div>
                      <ul className="text-[12px] text-[#C9C6C1] space-y-1">
                        <li>· Full content production</li>
                        <li>· Multi-platform management</li>
                        <li>· Campaign planning & strategy</li>
                        <li>· Priority production queue</li>
                      </ul>
                    </div>
                  </>
                )}

                {/* Contract length */}
                <Field label="Contract length">
                  <ChipPicker
                    options={[{v:3,l:"3 mo"},{v:6,l:"6 mo"},{v:12,l:"12 mo"}]}
                    value={months}
                    onChange={setMonths}
                    testIdPrefix="contract-months"
                  />
                </Field>
              </div>
            </Card>

            {/* Margin slider — admin only */}
            {isAdmin && (
              <Card>
                <div className="px-5 py-4 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783]">Adjust Margin</div>
                    <div className="text-[12px] text-[#8A8783]">Internal control — not shown to client.</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {MARGIN_PRESETS.map(p => (
                      <button
                        key={p}
                        onClick={() => setMarkup(p)}
                        className={`px-3 py-1.5 rounded-md text-[12px] tabular-nums transition-all ${markup === p ? "bg-white text-[#000000]" : "bg-white/[0.04] text-[#C9C6C1] hover:bg-white/[0.08]"}`}
                        data-testid={`margin-${p}`}
                      >
                        +{p}%
                      </button>
                    ))}
                    <Input type="number" value={markup} onChange={(e) => setMarkup(Number(e.target.value) || 0)} className="w-20 bg-[#000000] border-white/[0.08] text-white tabular-nums h-8 text-center" data-testid="margin-custom" />
                  </div>
                </div>
              </Card>
            )}

            {/* Notes */}
            <Card>
              <div className="px-5 py-4">
                <Field label="Internal Notes">
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="bg-[#000000] border-white/[0.08] text-white" />
                </Field>
              </div>
            </Card>
          </div>

          {/* RIGHT — Apple-style summary */}
          <aside>
            <div className="rounded-[20px] border border-white/[0.08] bg-[#000000] sticky top-6 overflow-hidden">
              <div className="px-5 py-5 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-3">
                  <Sparkles className="w-3 h-3" />
                  AI Content Engine
                </div>
                <div className="text-[15px] font-semibold text-[#F4F2EE] mb-1">
                  {breakdown.planLabel}
                </div>
                <div className="text-[12px] text-[#8A8783]">
                  {breakdown.reelsPerMonth} reels / month{breakdown.platforms > 0 ? ` · ${breakdown.platforms} platform${breakdown.platforms > 1 ? "s" : ""}` : ""}
                </div>
              </div>

              <div className="px-5 py-5 border-b border-white/[0.06]">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783]">Monthly Investment</div>
                <div className="text-[32px] font-light tabular-nums text-white mt-1" data-testid="monthly-client">{fmt(breakdown.monthlyClient)}</div>
                <div className="text-[12px] text-[#8A8783] mt-1.5">
                  Total over {months} mo: <span className="text-[#D5D3CE] tabular-nums">{fmt(totals.totalClient)}</span>
                </div>
              </div>

              <div className="px-5 py-4 border-b border-white/[0.06] space-y-1.5">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-1.5">Deliverables</div>
                {breakdown.deliverables.map((d, i) => (
                  <div key={i} className="text-[12px] text-[#C9C6C1]">· {d}</div>
                ))}
              </div>

              {showInternalView && (
                <div className="px-5 py-4 space-y-2 bg-[#000000]">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#565350] mb-2">Internal · admin only</div>
                  <Row k="Vendor cost" v={fmt(totals.totalVendor)} testId="vendor-total" />
                  <Row k="Profit" v={fmt(totals.profit)} testId="profit-total" />
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-[#8A8783]">Margin</span>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tier.color }} />
                      <span className="text-[#F4F2EE] tabular-nums font-semibold" data-testid="margin-percent">{totals.margin.toFixed(1)}%</span>
                      <span className="text-[10px] text-[#8A8783] uppercase tracking-wider" data-testid="margin-tier">{tier.label}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {proposalHtml && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <span className="text-sm font-semibold text-gray-800">Client-Facing Proposal Preview</span>
              <Button size="sm" variant="ghost" onClick={() => setProposalHtml(null)}><X className="w-4 h-4" /></Button>
            </div>
            <iframe srcDoc={proposalHtml} title="Proposal" className="w-full flex-1 border-0" data-testid="proposal-iframe" />
          </div>
        </div>
      )}
    </div>
  );
}

// ===== sub components =====

function Card({ children }) {
  return <div className="rounded-2xl border border-white/[0.08] bg-[#000000] overflow-hidden">{children}</div>;
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
function Row({ k, v, testId }) {
  return (
    <div className="flex justify-between text-[12px]">
      <span className="text-[#8A8783]">{k}</span>
      <span className="text-[#F4F2EE] tabular-nums" data-testid={testId}>{v}</span>
    </div>
  );
}

function PlanCard({ active, onClick, icon: Icon, title, tagline, description, testId }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-2xl border transition-all duration-200 p-5 ${
        active
          ? "border-[#F4F2EE] bg-[#121212] ring-1 ring-white/20"
          : "border-white/[0.08] bg-[#000000] hover:border-white/[0.16] hover:bg-[#121212]"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${active ? "bg-white text-[#000000]" : "bg-white/[0.04] text-[#8A8783]"}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1">
          <div className="text-[14px] font-semibold text-[#F4F2EE]">{title}</div>
          <div className="text-[11px] text-[#8A8783]">{tagline}</div>
        </div>
      </div>
      <p className="text-[12px] leading-relaxed text-[#8A8783]">{description}</p>
    </button>
  );
}

function ChipPicker({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(opt => (
        <button
          key={String(opt.v)}
          onClick={() => onChange(opt.v)}
          className={`px-3 py-2 rounded-lg text-[12px] tabular-nums transition-all ${
            value === opt.v
              ? "bg-white text-[#000000] font-semibold"
              : "bg-white/[0.04] text-[#C9C6C1] hover:bg-white/[0.08]"
          }`}
          data-testid={`${testIdPrefix}-${opt.v}`}
        >
          {opt.l}
        </button>
      ))}
    </div>
  );
}

function Stepper({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-[#8A8783] mb-1.5 block">{label}</Label>
      <div className="flex items-center gap-1 bg-[#000000] border border-white/[0.08] rounded-md px-2 py-1">
        <button onClick={() => onChange(Math.max(0, value - 1))} className="text-[#8A8783] hover:text-white"><Minus className="w-3 h-3" /></button>
        <span className="flex-1 text-center text-[13px] text-[#F4F2EE] tabular-nums">{value}</span>
        <button onClick={() => onChange(value + 1)} className="text-[#8A8783] hover:text-white"><Plus className="w-3 h-3" /></button>
      </div>
    </div>
  );
}
